import {
	type EmissionValueAnsweredUnit,
	EmissionValueUnit,
} from '@api-client/api'
import type { TFunction } from 'i18next'
import { ceil, round as _round, toNumber } from 'lodash-es'

import { isValidArray } from './arrays'

export interface IAutoNumberFormatterOptions {
	locale?: string
	suffix?: boolean
	decimals?: number
	removeZeroDecimals?: boolean
	suffixes?: readonly { factor: number; isDefault?: boolean; suffix: string }[]
	extraSuffix?: string
}

const defaultUnits = [
	{ factor: 1_000_000, suffix: ' M' },
	{ factor: 1000, suffix: ' k' },
	{ factor: 1, suffix: '' },
] as const

export const weightUnits = [
	{ factor: 1_000_000_000_000, suffix: ' Gt' },
	{ factor: 1_000_000_000, suffix: ' Mt' },
	{ factor: 1_000_000, suffix: ' kt' },
	{ factor: 1000, suffix: ' t' },
	{ factor: 1, isDefault: true, suffix: ' kg' },
	{ factor: 0.001, suffix: ' g' },
	{ factor: 0.000001, suffix: ' mg' },
]

const autoNumberFormatter = (
	num: string | number,
	{
		locale = 'en-US',
		decimals: _decimals,
		removeZeroDecimals = false,
		suffixes = defaultUnits,
		extraSuffix = 'kg',
	}: IAutoNumberFormatterOptions = {}
) => {
	if (!isNumber(num) && !isNumberStr(num)) {
		return num
	}
	const _num = Number(num)
	const absNum = Math.abs(_num)

	for (const [index, { factor, isDefault, suffix }] of suffixes.entries()) {
		if (
			absNum < factor &&
			index < suffixes.length - 1 &&
			(absNum > 0 || !isDefault)
		) {
			continue
		}
		const decimals = isNumber(_decimals)
			? _decimals
			: getDecimalPlaces(_num / (suffix ? factor : 1))

		const formattedNumber = toEnStr(_num / factor, { decimals, locale })

		return (
			(removeZeroDecimals
				? removeAllZeroDecimals(formattedNumber)
				: formattedNumber) +
			(suffix + extraSuffix)
		)
	}
	return num + ''
}

const formatEmissions = (
	value: number,
	perUnit: EmissionValueUnit | EmissionValueAnsweredUnit | undefined,
	t: TFunction<'translation', string>
) => {
	if (perUnit === EmissionValueUnit.OTHER) {
		return toEnStr(value)
	}
	return autoNumberFormatter(value, {
		suffixes: weightUnits,
		extraSuffix: t('emissions.unit.name', {
			context: perUnit || EmissionValueUnit.KGCO2_EQ_KG,
		}).replace(/^kg/, ''),
	})
}

const getDecimalPlaces = (num: number, max = 2) =>
	//get the number of decimal places in the number passed, capped to a maximum of 2
	Math.min(
		max,
		Math.floor(num) === num ? 0 : (getDecimalPartFromNumber(num) || '').length
	)

const getNumberLength = (val: number | string) => {
	if (isNumber(val)) {
		return String(val).replace('.', '').length
	} else if (typeof val === 'string') {
		return val.replace('.', '').length
	}
	return 0
}

// if val = 1.00 -> 1 else if val = 1.01 -> 1.01
const removeAllZeroDecimals = (val: string) => {
	const [int, decs] = val.split('.')

	if (!decs) {
		return val
	}
	const suffix = decs.match(/[a-z\s]/gi)?.join('') ?? ''
	const isAllZeroes = decs.replaceAll(/[0\D]+/gi, '').length === 0

	return isAllZeroes ? `${int}${suffix}` : val
}

const getDecimalPartFromNumber = (num: string | number) => {
	if (isNumeric(num)) {
		return String(num).split('.')[1]
	}
	return undefined
}

const round = (num: number, factor: number) =>
	Math.round((num + Number.EPSILON) * factor) / factor

interface IToEnStrProps {
	locale?: string
	decimals?: number
}

interface FormatPercentageOptions {
	locale?: string
	minimumFractionDigits?: number
	maximumFractionDigits?: number
	suffix?: string
}

const DEFAULT_PERCENTAGE_SUFFIX = ' %'

const toEnStr = (
	num: number,
	{ decimals = 0, locale = 'en-US' }: IToEnStrProps = {}
) =>
	num.toLocaleString(locale, {
		minimumFractionDigits: decimals,
		maximumFractionDigits: decimals,
	})

const numberWithCommas = (x: number | string) => {
	if (isNumeric(x)) {
		const _x = toNumber(x)
		return _x
			.toFixed(0)
			.toString()
			.replaceAll(/\B(?=(\d{3})+(?!\d))/g, ',')
	}
	return `${x}`.replaceAll(/\B(?=(\d{3})+(?!\d))/g, ',')
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isNumeric = (val: any) => /^-?[\d.]+(?:e-?\d+)?$/.test(val)

const isValue = (val: unknown) => val !== null && val !== undefined

const isNumberStr = (val: unknown) => isNumber(Number(val)) && isValue(val)

const isNaN = (val: unknown) => Number.isNaN(val)

const isNumber = (val: unknown): val is number =>
	typeof val === 'number' && !isNaN(val)

const isNumberOrNumberStr = (val: unknown) => isNumber(val) || isNumberStr(val)

const percentageAutoRounding = (x: number | string | null | undefined) => {
	if (isNumeric(x)) {
		let _finalVal = toNumber(x) * 100
		const _decimalPart = getDecimalPartFromNumber(_finalVal)
		if (_decimalPart) {
			if (_finalVal >= 10) {
				_finalVal = toNumber(numberRounding(_finalVal, 0))
			} else {
				_finalVal = toNumber(numberRounding(_finalVal, 1))
			}
		}
		return _finalVal / 100
	}
	return x
}

const numberRounding = (x: number | string, r: number) => {
	if (isNumeric(x)) {
		const _x = toNumber(x)
		return _round(_x, r || 0)
	}
	return x
}

const numberRoundingUP = (x: number | string, r: number) => {
	if (isNumeric(x)) {
		const _x = toNumber(x)
		return ceil(_x, r || 0)
	}
	return x
}

const getQuantileSorted = (arr: readonly number[], quantile: number) => {
	const pos = (arr.length - 1) * quantile
	const base = Math.floor(pos)
	const rest = pos - base
	if (arr[base + 1] !== undefined) {
		return arr[base] + rest * (arr[base + 1] - arr[base])
	}
	return arr[base]
}

//array must be sorted
const getQuantile = (_arr: readonly number[], quantile: number) => {
	const arr = _arr.toSorted((a, b) => a - b)
	return getQuantileSorted(arr, quantile)
}

const getSortedMinAndMax = (arr: readonly number[]) =>
	isValidArray(arr) ? [arr[0], arr[arr.length - 1]] : []

const getMinAndMax = (arr: readonly number[]) =>
	isValidArray(arr) ? [Math.min(...arr), Math.max(...arr)] : []

const multiplyDecimalNumberWith100 = (num: string | number) => {
	// function will be useful to solve the decimal error (js can not handle decimal value multiply with 100)
	// normal multiplication add addition decimal like 00000000000001 e.x. 0.56*100 = 56.00000000000001
	// this issue never occur always but happens some time
	if (isNumeric(num)) {
		const _val = toNumber(num)
		return +(_val * 100).toFixed(1)
	}
	return +num
}

const calcPercentage = (total: number, value: number) =>
	total === 0 ? 0 : (value * 100) / total

const formatPercentage = (
	value: number | null | undefined,
	{
		locale = 'en-US',
		minimumFractionDigits = 0,
		maximumFractionDigits = 1,
		suffix = DEFAULT_PERCENTAGE_SUFFIX,
	}: FormatPercentageOptions = {}
) => {
	if (typeof value !== 'number' || !Number.isFinite(value)) {
		return undefined
	}

	return `${value.toLocaleString(locale, {
		minimumFractionDigits,
		maximumFractionDigits,
	})}${suffix}`
}

export {
	autoNumberFormatter,
	calcPercentage,
	formatEmissions,
	formatPercentage,
	getDecimalPartFromNumber,
	getMinAndMax,
	getNumberLength,
	getQuantile,
	getQuantileSorted,
	getSortedMinAndMax,
	isNaN,
	isNumber,
	isNumberOrNumberStr,
	isNumberStr,
	isNumeric,
	multiplyDecimalNumberWith100,
	numberRounding,
	numberRoundingUP,
	numberWithCommas,
	percentageAutoRounding,
	round,
}
